import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { baseQuery } from '@app/store/baseQuery';
import serverRoutes from '@app/utils/server-routes';
import {
  MonitoringFormCreateType,
  MonitoringFormCriteriaBlockCreateType,
  MonitoringFormCriteriaBlockObjectType,
  MonitoringFormCriteriaBulkType,
  MonitoringFormCriteriaCreateType,
  MonitoringFormCriteriaObjectType,
  MonitoringFormInfoType,
  MonitoringFormsBulkResponseType,
} from '@app/interfaces/qm-module/qm-manage/qm-monitoring-forms.type';

export const qmManageMonitoringFormsApi = createApi({
  reducerPath: 'qmManageMonitoringFormsApi',
  tagTypes: [
    'qmMonitoringFormsList',
    'qmMonitoringForm',
    'qmMonitoringFormBlocksList',
    'qmMonitoringFormBlock',
    'qmMonitoringFormCriteriaList',
    'qmMonitoringFormCriteria',
  ],
  baseQuery: baseQuery,
  endpoints: (build) => ({
    getMonitoringFormsBulk: build.query<
      MonitoringFormsBulkResponseType,
      Partial<{ project_id?: string }>
    >({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_bulk,
        params,
      }),
      providesTags: ['qmMonitoringFormsList'],
    }),
    getMonitoringFormSingle: build.query<MonitoringFormInfoType, { id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms,
        params,
      }),
      providesTags: ['qmMonitoringForm'],
    }),
    createMonitoringForm: build.mutation<MonitoringFormInfoType, MonitoringFormCreateType>({
      query: (body) => ({
        url: serverRoutes.qm_manage_monitoring_forms,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['qmMonitoringFormsList'],
    }),
    editMonitoringForm: build.mutation<
      MonitoringFormInfoType,
      { params: { id: string }; body: MonitoringFormCreateType }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.qm_manage_monitoring_forms,
        method: 'PUT',
        body,
        params,
      }),
      invalidatesTags: ['qmMonitoringFormsList', 'qmMonitoringForm'],
    }),
    deleteMonitoringForm: build.mutation<MonitoringFormInfoType, { id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms,
        method: 'DELETE',
        params,
      }),
      invalidatesTags: ['qmMonitoringFormsList'],
    }),
    cloneMonitoringForm: build.mutation<MonitoringFormInfoType, { id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_clone,
        method: 'POST',
        params,
      }),
      invalidatesTags: ['qmMonitoringFormsList'],
    }),
    changeMonitoringFormStatus: build.mutation<
      MonitoringFormInfoType,
      { id: string; status: MonitoringFormInfoType['status'] }
    >({
      query: ({ id, status }) => ({
        url: serverRoutes.qm_manage_monitoring_forms_status,
        method: 'PUT',
        params: { id },
        body: { status },
      }),
      invalidatesTags: ['qmMonitoringFormsList', 'qmMonitoringForm'],
    }),
    // CRITERIA_APIS
    // CRITERIA_BLOCKS
    getMFBlocksBulk: build.query<MonitoringFormCriteriaBlockObjectType[], { id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_blocks_bulk,
        params,
      }),
      providesTags: ['qmMonitoringFormBlocksList'],
    }),
    getMFBlockSingle: build.query<MonitoringFormCriteriaBlockObjectType, { id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_blocks,
        params,
      }),
      providesTags: ['qmMonitoringFormBlock'],
    }),
    createMFBlock: build.mutation<
      MonitoringFormCriteriaBlockObjectType,
      MonitoringFormCriteriaBlockCreateType
    >({
      query: (body) => ({
        url: serverRoutes.qm_manage_monitoring_forms_blocks,
        body,
        method: 'POST',
      }),
      invalidatesTags: ['qmMonitoringFormBlocksList', 'qmMonitoringFormCriteriaList'],
    }),
    editMFBlock: build.mutation<
      MonitoringFormCriteriaBlockObjectType,
      { body: Omit<MonitoringFormCriteriaBlockCreateType, 'mform_id'>; params: { id: string } }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.qm_manage_monitoring_forms_blocks,
        body,
        params,
        method: 'PUT',
      }),
      invalidatesTags: [
        'qmMonitoringFormBlocksList',
        'qmMonitoringFormBlock',
        'qmMonitoringFormCriteriaList',
      ],
    }),
    deleteMFBlock: build.mutation<MonitoringFormCriteriaBlockObjectType, { id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_blocks,
        params,
        method: 'DELETE',
      }),
      invalidatesTags: ['qmMonitoringFormBlocksList', 'qmMonitoringFormCriteriaList'],
    }),
    // CRITERIA_BLOCKS
    // CRITERIA_APIS

    getMFCriteriaBulk: build.query<MonitoringFormCriteriaBulkType, { mform_id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_criteria_bulk,
        params,
      }),
      providesTags: ['qmMonitoringFormCriteriaList'],
    }),
    getMFCriteriaInfoSingle: build.query<MonitoringFormCriteriaObjectType, { id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_criteria,
        params,
      }),
      providesTags: ['qmMonitoringFormCriteria'],
    }),
    createMFCriteria: build.mutation<
      MonitoringFormCriteriaBlockObjectType[],
      {
        params: { cblock_id?: string; mform_id: string };
        body: MonitoringFormCriteriaCreateType<'AUTO'> | MonitoringFormCriteriaCreateType<'MANUAL'>;
      }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.qm_manage_monitoring_forms_criteria,
        params,
        body,
        method: 'POST',
      }),
      invalidatesTags: ['qmMonitoringFormCriteriaList', 'qmMonitoringForm'],
    }),
    editMFCriteria: build.mutation<
      MonitoringFormCriteriaBlockObjectType[],
      {
        params: { cblock_id?: string; mform_id: string; id: string };
        body: MonitoringFormCriteriaCreateType<'AUTO'> | MonitoringFormCriteriaCreateType<'MANUAL'>;
      }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.qm_manage_monitoring_forms_criteria,
        params,
        body,
        method: 'PUT',
      }),
      invalidatesTags: [
        'qmMonitoringFormCriteriaList',
        'qmMonitoringFormCriteria',
        'qmMonitoringForm',
      ],
    }),
    deleteMFCriteria: build.mutation<MonitoringFormCriteriaBlockObjectType[], { id: string }>({
      query: (params) => ({
        url: serverRoutes.qm_manage_monitoring_forms_criteria,
        params,
        method: 'DELETE',
      }),
      invalidatesTags: ['qmMonitoringFormCriteriaList', 'qmMonitoringForm'],
    }),
    orderMFCriteria: build.mutation<
      MonitoringFormCriteriaBlockObjectType[],
      { params: { id: string; cblock_id?: string }; body: { order: number } }
    >({
      query: ({ params, body }) => ({
        url: serverRoutes.qm_manage_monitoring_forms_order,
        params,
        body,
        method: 'PUT',
      }),
      invalidatesTags: ['qmMonitoringFormCriteriaList'],
    }),
    // CRITERIA_APIS

    //block orders
    orderMFCriteriaBlock: build.mutation<
      MonitoringFormCriteriaBlockObjectType[],
      { params: { id: string }; body: { order: number } }
    >({
      query: ({ params, body }) => ({
        url: serverRoutes.qm_manage_monitoring_forms_block_order,
        params,
        body,
        method: 'PUT',
      }),
      invalidatesTags: ['qmMonitoringFormCriteriaList'],
    }),

    //block orders
  }),
});

export const {
  useGetMonitoringFormsBulkQuery,
  useGetMonitoringFormSingleQuery,
  useLazyGetMonitoringFormSingleQuery,
  useCreateMonitoringFormMutation,
  useEditMonitoringFormMutation,
  useDeleteMonitoringFormMutation,
  useCloneMonitoringFormMutation,
  useChangeMonitoringFormStatusMutation,
  // CRITERIA_BLOCKS_API
  useGetMFBlocksBulkQuery,
  useLazyGetMFBlockSingleQuery,
  useDeleteMFBlockMutation,
  useEditMFBlockMutation,
  useCreateMFBlockMutation,
  // CRITERIA_BLOCKS_API
  // CRITERIA_API
  useGetMFCriteriaBulkQuery,
  useLazyGetMFCriteriaInfoSingleQuery,
  useCreateMFCriteriaMutation,
  useEditMFCriteriaMutation,
  useDeleteMFCriteriaMutation,
  useOrderMFCriteriaMutation,
  // CRITERIA_API
  useOrderMFCriteriaBlockMutation,
} = qmManageMonitoringFormsApi;
